import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, Handshake, ShieldCheck, MapPin, Package, Wrench, Clock, ExternalLink, Flame, Pencil, Search } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import ImageCarousel from '@/components/ImageCarousel';
import api from '@/lib/api';

const conditionLabels = { nuevo:'Nuevo', como_nuevo:'Como nuevo', bueno:'Buen estado', usado:'Usado' };
const locTypeLabels = { store:'Local Establecido', home:'A Domicilio', digital:'Digital / Remoto' };

export default function ItemPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    api.get(`/items/${itemId}`).then(r => { setItem(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get('/auth/me').then(r => { setCurrentUser(r.data); setAuthChecked(true); }).catch(() => setAuthChecked(true));
  }, [itemId]);

  const isOwner = currentUser && item && currentUser.user_id === item.seller_id;
  const isLoggedIn = !!currentUser;
  const isSvc = item?.item_type === 'service';
  const isWanted = item?.item_type === 'wanted';
  const isSold = item?.status === 'sold' && !isSvc;

  const handleAction = () => {
    if (!isLoggedIn) {
      sessionStorage.setItem('bisne_next', `/item/${itemId}`);
      navigate(`/login?next=${encodeURIComponent(`/item/${itemId}`)}`);
    } else if (isWanted) {
      navigate(`/deal/${itemId}?preMsg=${encodeURIComponent('Hola, vi que buscas esto. Yo tengo algo que te puede interesar.')}`);
    } else {
      navigate(`/deal/${itemId}`);
    }
  };

  const handleShare = async () => {
    if (!item) return;
    const url = `${window.location.origin}/item/${item.item_id}`;
    if (navigator.share) { try { await navigator.share({ title: item.title, url }); } catch {} }
    else { try { await navigator.clipboard.writeText(url); toast.success('Link copiado!'); } catch {} }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!item) return <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8"><p className="text-muted-foreground font-body">Articulo no encontrado</p><Button onClick={() => navigate('/')} className="mt-4 rounded-sm font-body">VOLVER</Button></div>;

  const mapsUrl = item.service_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.service_address)}` : null;

  return (
    <div data-testid="item-page" className="min-h-screen bg-background page-with-nav">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b-2 border-border px-4 py-3 flex items-center justify-between">
        <button data-testid="item-back-btn" onClick={() => navigate(-1)} className="min-w-[44px] min-h-[44px] flex items-center justify-center"><ArrowLeft className="w-6 h-6 text-foreground" /></button>
        <div className="flex gap-2">
          <button data-testid="item-share-btn" onClick={handleShare} className="min-w-[44px] min-h-[44px] flex items-center justify-center"><Share2 className="w-5 h-5 text-foreground" /></button>
          <button data-testid="item-like-btn" onClick={() => setLiked(!liked)} className="min-w-[44px] min-h-[44px] flex items-center justify-center"><Heart className={`w-5 h-5 transition-all ${liked ? 'fill-red-500 text-red-500' : 'text-foreground'}`} /></button>
        </div>
      </div>

      <div className="max-w-lg mx-auto">
        {/* Image/Carousel area */}
        {isWanted ? (
          /* Wanted items: inverted card header */
          <div className="bg-[#FFD700] text-black p-6 relative">
            <Badge className="rounded-sm bg-black text-[#FFD700] border-0 font-body text-[10px] uppercase tracking-wider mb-3">
              <Search className="w-3 h-3 mr-1" />Se Busca
            </Badge>
            <h1 data-testid="item-title" className="font-heading text-3xl uppercase">{item.title}</h1>
            <p className="font-heading text-2xl mt-2">Presupuesto: ${item.price?.toLocaleString()} MXN</p>
            {item.images?.length > 0 && (
              <div className="mt-4">
                <ImageCarousel images={item.images} videoUrl={item.video_url} className="w-full aspect-video bg-black/10 rounded-sm overflow-hidden" />
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <ImageCarousel images={item.images || []} videoUrl={item.video_url} className={`w-full aspect-square bg-zinc-900 ${isSold ? 'grayscale' : ''}`} />
            {/* SOLD STAMP */}
            {isSold && (
              <div data-testid="item-sold-badge" className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="bg-red-600 px-8 py-4 border-4 border-red-800 shadow-2xl" style={{ transform: 'rotate(-15deg)' }}>
                  <span className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-wider block text-center leading-none">YA SE ARMO!</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-4 py-5 space-y-4">
          {/* Type badges + offer count */}
          {!isWanted && (
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {isSvc ? (
                    <Badge className="rounded-sm bg-orange-500/20 text-orange-400 border-orange-500/30 font-body text-[10px] uppercase tracking-wider"><Wrench className="w-3 h-3 mr-1" />Servicio</Badge>
                  ) : (
                    <Badge className="rounded-sm bg-primary/20 text-primary border-primary/30 font-body text-[10px] uppercase tracking-wider"><Package className="w-3 h-3 mr-1" />Producto</Badge>
                  )}
                  {!isSvc && item.condition && (
                    <Badge className="rounded-sm bg-zinc-800/80 text-zinc-300 border-zinc-700 font-body text-[10px] uppercase tracking-wider">{conditionLabels[item.condition]||item.condition}</Badge>
                  )}
                  {item.location && (
                    <Badge className="rounded-sm bg-zinc-800/80 text-zinc-300 border-zinc-700 font-body text-[10px]">
                      <MapPin className="w-3 h-3 mr-0.5" />{item.location}
                    </Badge>
                  )}
                  {item.offer_count > 0 && (
                    <Badge data-testid="item-offer-count" className="rounded-sm bg-red-500/20 text-red-400 border-red-500/30 font-body text-[10px] uppercase tracking-wider"><Flame className="w-3 h-3 mr-1" />{item.offer_count} oferta{item.offer_count > 1 ? 's' : ''}</Badge>
                  )}
                </div>
                <h1 data-testid="item-title" className="font-heading text-3xl text-foreground uppercase">{item.title}</h1>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading text-3xl text-primary">${item.price?.toLocaleString()}</p>
                {isSvc && <p className="text-muted-foreground text-xs font-body">Costo Base</p>}
              </div>
            </div>
          )}

          {/* Wanted description */}
          {isWanted && (
            <div className="space-y-2">
              {item.location && (
                <Badge className="rounded-sm bg-zinc-800/80 text-zinc-300 border-zinc-700 font-body text-[10px]">
                  <MapPin className="w-3 h-3 mr-0.5" />{item.location}
                </Badge>
              )}
              {item.offer_count > 0 && (
                <Badge data-testid="item-offer-count" className="rounded-sm bg-red-500/20 text-red-400 border-red-500/30 font-body text-[10px] uppercase tracking-wider ml-2"><Flame className="w-3 h-3 mr-1" />{item.offer_count} oferta{item.offer_count > 1 ? 's' : ''}</Badge>
              )}
            </div>
          )}

          {item.description && <p className="text-muted-foreground font-body text-sm">{item.description}</p>}

          {/* Service details */}
          {isSvc && (
            <div className="bg-card border-2 border-border rounded-sm p-4 space-y-3">
              {item.service_location_type && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-400 shrink-0" />
                  <span className="font-body text-sm text-foreground font-medium">{locTypeLabels[item.service_location_type] || item.service_location_type}</span>
                </div>
              )}
              {item.service_address && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="item-maps-link"
                  className="flex items-center gap-2 text-primary hover:underline">
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span className="font-body text-sm">{item.service_address}</span>
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
              )}
              {item.service_hours && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-400 shrink-0" />
                  <span className="font-body text-sm text-foreground">{item.service_hours}</span>
                </div>
              )}
            </div>
          )}

          <Separator />

          {/* Seller */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/user/${item.seller_id}`)} data-testid="item-seller-link">
            <Avatar className="w-12 h-12 border-2 border-border">
              <AvatarImage src={item.seller_picture} />
              <AvatarFallback className="bg-secondary text-foreground font-body">{item.seller_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-body font-bold text-foreground text-sm">{item.seller_name}</p>
              <p className="text-muted-foreground text-xs font-body">{isWanted ? 'Buscador' : isSvc ? 'Proveedor' : 'Vendedor'}</p>
            </div>
            <ShieldCheck className="w-5 h-5 text-primary" />
          </div>

          {/* Action buttons */}
          {authChecked && isOwner ? (
            <div className="space-y-3">
              <div data-testid="item-owner-badge" className="w-full h-14 rounded-sm border-2 border-primary/30 bg-primary/5 flex items-center justify-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <span className="font-body font-bold text-primary uppercase text-sm">{isWanted ? 'Tu Busqueda' : 'Tu Articulo'}</span>
              </div>
              <Button data-testid="item-edit-btn" onClick={() => navigate(`/edit/${item.item_id}`)} variant="outline"
                className="w-full h-12 rounded-sm font-body font-bold uppercase border-border text-foreground">
                <Pencil className="w-4 h-4 mr-2" /> EDITAR
              </Button>
            </div>
          ) : isSold ? (
            <div data-testid="item-sold-action" className="w-full h-14 rounded-sm bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center gap-2">
              <span className="font-body font-bold text-zinc-400 uppercase text-sm">Ya no disponible</span>
            </div>
          ) : isWanted ? (
            <Button data-testid="item-yo-lo-tengo-btn" onClick={handleAction}
              className="w-full h-14 rounded-sm font-body font-bold uppercase text-base bg-[#FFD700] text-black hover:bg-[#FFD700]/90 border-2 border-black">
              <Handshake className="w-5 h-5 mr-2" /> YO LO TENGO
            </Button>
          ) : isSvc ? (
            <Button data-testid="item-contactar-btn" onClick={handleAction}
              className="w-full h-14 rounded-sm font-body font-bold uppercase text-base bg-orange-500 text-white hover:bg-orange-600 border-2 border-orange-500">
              <Wrench className="w-5 h-5 mr-2" /> CONTACTAR
            </Button>
          ) : (
            <Button data-testid="item-negociar-btn" onClick={handleAction}
              className="w-full h-14 rounded-sm font-body font-bold uppercase text-base bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-primary">
              <Handshake className="w-5 h-5 mr-2" /> NEGOCIAR
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
